import { useEffect, useState, useRef } from "react";
import { CalendarCheck, Phone, CheckCircle, Star } from "lucide-react";

const TM_METRICS = [
  { iconSrc: "/images/happy-patients.png", iconAlt: "Happy Patients icon", target: 8000, suffix: "+", label: "Happy Patients" },
  { iconSrc: "/images/experience.png",     iconAlt: "Years Experience icon", target: 10,   suffix: "+", label: "Years Experience" },
  { iconSrc: "/images/surgeries-performed.png", iconAlt: "Successful Surgeries icon", target: 5000, suffix: "+", label: "Successful Surgeries" },
  { iconSrc: "/images/rating.png",         iconAlt: "Patient Rating icon", target: 4.9,  suffix: "★", label: "Patient Rating", decimal: true },
];

function AnimatedCounter({ target, suffix, decimal, inView }) {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);

  useEffect(() => {
    if (!inView || hasRun.current) return;
    hasRun.current = true;
    const dur = 2000;
    const steps = 60;
    let step = 0;
    const t = setInterval(() => {
      step++;
      const cur = (target / steps) * step;
      if (step >= steps) {
        setCount(target);
        clearInterval(t);
      } else {
        setCount(decimal ? Math.round(cur * 10) / 10 : Math.floor(cur));
      }
    }, dur / steps);
    return () => clearInterval(t);
  }, [inView, target, decimal]);

  return (
    <span style={{
      fontFamily: "Montserrat, sans-serif",
      fontWeight: 800,
      fontSize: "clamp(1.5rem, 2.2vw, 1.9rem)",
      color: "#1E3A5F",
      lineHeight: 1,
      letterSpacing: "-0.02em",
    }}>
      {decimal ? count.toFixed(1) : count.toLocaleString()}{suffix}
    </span>
  );
}

export default function HeroSection({ onBookAppointment }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 120); }, []);
  const [tmInView, setTmInView] = useState(false);
  const tmRef = useRef(null);
  useEffect(() => {
    const el = tmRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setTmInView(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      {/* ═══════════ MOBILE HERO — isolated, zero desktop influence ═══════════ */}
      <section
        id="hero"
        data-testid="hero-section"
        className="md:hidden"
        style={{ marginTop: "80px", position: "relative", overflow: "hidden", minHeight: "auto" }}
      >
        {/* Mobile bg: layer-1 */}
        <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
          <img src="/images/doctor/layer-1.png" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} loading="eager" fetchPriority="high" />
        </div>
        {/* Mobile content */}
        <div style={{ position: "relative", zIndex: 2, padding: "28px 20px 0" }}>
          {/* Badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 18,
            background: "rgba(255,255,255,0.85)",
            border: "1px solid rgba(46,111,184,0.18)", borderRadius: 999, padding: "6px 14px" }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#2BBBAD" }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.10em", color: "#1F4E79", textTransform: "uppercase" }}>
              Surgical Excellence · Vijayawada
            </span>
          </div>
          {/* Name */}
          <h1 style={{ fontSize: "2.5rem", fontWeight: 800, lineHeight: 1.05, color: "#1F4E79", marginBottom: 10 }}>
            Dr. Dileep<br /><span style={{ color: "#1F4E79" }}>Sunkavalli</span>
          </h1>
          {/* Specialty */}
          <p style={{ fontSize: "1rem", fontWeight: 600, fontStyle: "italic", color: "#2E6FB8", marginBottom: 14 }}>
            General, Laparoscopic &amp; Laser Surgeon
          </p>
          {/* Qualifications */}
          <div style={{ display: "inline-block", marginBottom: 18,
            background: "rgba(255,255,255,0.85)",
            border: "1px solid rgba(46,111,184,0.14)", borderRadius: 10, padding: "7px 14px" }}>
            <p style={{ fontSize: 11, color: "#5A6E7F", fontWeight: 500 }}>
              MBBS · MS (General Surgery) · FMAS · FIAGES · FALS (Hernia) · EFIAGES
            </p>
          </div>
          {/* Checklist */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px", marginBottom: 22 }}>
            {["Laparoscopic Surgery", "Laser Piles, Fissure & Fistula", "Hernia & Gallbladder", "Endoscopy & Colonoscopy"].map((s) => (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 500, color: "#0F2A3D" }}>
                <CheckCircle style={{ width: 14, height: 14, color: "#2BBBAD", flexShrink: 0 }} strokeWidth={2.5} />{s}
              </div>
            ))}
          </div>
          {/* CTAs */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button data-testid="hero-book-btn" onClick={onBookAppointment}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 600, fontSize: 13.5,
                background: "linear-gradient(135deg, #2F6FE4 0%, #1E4DB7 100%)", color: "#fff",
                padding: "12px 20px", borderRadius: 12, border: "none",
                boxShadow: "0 8px 20px rgba(47,111,228,0.25)", cursor: "pointer" }}>
              <CalendarCheck style={{ width: 16, height: 16 }} strokeWidth={1.5} />Book Appointment
            </button>
            <a href="tel:+917075659696" data-testid="hero-call-btn"
              style={{ display: "inline-flex", alignItems: "center", gap: 7, fontWeight: 600, fontSize: 13.5,
                background: "rgba(255,255,255,0.85)",
                color: "#1F4E79", padding: "12px 20px", borderRadius: 12,
                border: "1px solid rgba(46,111,184,0.22)", textDecoration: "none" }}>
              <Phone style={{ width: 16, height: 16 }} strokeWidth={1.5} />Call Now
            </a>
          </div>
        </div>
        {/* Mobile doctor image — full width, no box */}
        <div style={{ marginTop: 24, width: "100%", paddingBottom: "75%", position: "relative", zIndex: 3 }}>
          <img src="/images/doctor/hero-section-image.jpeg" alt="Dr. Dileep Sunkavalli"
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }} />
        </div>
      </section>

      {/* ═══════════ DESKTOP HERO — isolated, zero mobile influence ═══════════ */}
      <section
        id="hero-desktop"
        className="hidden md:block"
        style={{
          marginTop: "148px",
          position: "relative",
          overflow: "hidden",
          minHeight: "calc(100vh - 148px)",
          background: "radial-gradient(circle at 75% 50%, rgba(255,220,150,0.35), transparent 60%), radial-gradient(circle at 20% 30%, rgba(47,111,228,0.08), transparent 40%), linear-gradient(90deg, #F5F9FF 0%, #EAF2FB 45%, #F4EDE4 100%)"
        }}
      >
        {/* Doctor image — full-bleed right half, positioned absolutely */}
        <div style={{
          position: "absolute",
          top: 0, right: 0, bottom: 0,
          width: "55%",
          zIndex: 1,
          overflow: "hidden"
        }}>
          <img
            src="/images/doctor/hero-section-image.jpeg"
            alt="Dr. Dileep Sunkavalli"
            loading="eager"
            fetchPriority="high"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
              display: "block",
            }}
          />
          {/* White gradient fade on LEFT edge of image — soft blend into content */}
          <div style={{
            position: "absolute",
            top: 0, left: 0, bottom: 0,
            width: "45%",
            background: "linear-gradient(90deg, #F5F9FF 0%, rgba(245,249,255,0.95) 20%, rgba(245,249,255,0.6) 55%, transparent 100%)",
            zIndex: 2,
            pointerEvents: "none"
          }} />
        </div>

        {/* Left content — sits above the image */}
        <div style={{
          position: "relative",
          zIndex: 3,
          maxWidth: "1320px",
          margin: "0 auto",
          padding: "0 48px",
          minHeight: "calc(100vh - 148px)",
          display: "flex",
          alignItems: "center",
        }}>
          <div style={{ width: "50%", paddingTop: 48, paddingBottom: 48 }}>

            {/* Badge */}
            <div
              className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 24,
                background: "rgba(255,255,255,0.75)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                border: "1px solid rgba(46,111,184,0.18)", borderRadius: 999, padding: "6px 16px",
                boxShadow: "0 2px 12px rgba(46,111,184,0.10)", transitionDelay: "0ms" }}
            >
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#2BBBAD", boxShadow: "0 0 6px #2BBBAD" }} />
              <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.12em", color: "#1F4E79", textTransform: "uppercase" }}>
                Surgical Excellence · Vijayawada
              </span>
            </div>

            {/* Name */}
            <h1
              className={`font-heading font-bold tracking-tight transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ fontSize: "clamp(2.8rem, 4.5vw, 4rem)", lineHeight: 1.04, color: "#1F4E79", marginBottom: 12, transitionDelay: "80ms" }}
            >
              Dr. Dileep<br /><span style={{ color: "#1F4E79" }}>Sunkavalli</span>
            </h1>

            {/* Specialty */}
            <p
              className={`font-heading font-semibold italic transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.35rem)", color: "#2E6FB8", marginBottom: 18, lineHeight: 1.4, transitionDelay: "140ms" }}
            >
              General, Laparoscopic &amp; Laser Surgeon
            </p>

            {/* Qualifications */}
            <div
              className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ display: "inline-block", marginBottom: 28, transitionDelay: "190ms",
                background: "rgba(255,255,255,0.7)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(46,111,184,0.14)", borderRadius: 10, padding: "8px 16px" }}
            >
              <p style={{ fontSize: 12.5, color: "#5A6E7F", fontWeight: 500, letterSpacing: "0.02em" }}>
                MBBS · MS (General Surgery) · FMAS · FIAGES · FALS (Hernia) · EFIAGES
              </p>
            </div>

            {/* Checklist */}
            <div
              className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px", marginBottom: 36, transitionDelay: "240ms" }}
            >
              {["Laparoscopic Surgery", "Laser Piles, Fissure & Fistula", "Hernia & Gallbladder", "Endoscopy & Colonoscopy"].map((s) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 500, color: "#0F2A3D" }}>
                  <CheckCircle style={{ width: 16, height: 16, color: "#2BBBAD", flexShrink: 0 }} strokeWidth={2.5} />{s}
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div
              className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ display: "flex", gap: 12, flexWrap: "wrap", transitionDelay: "300ms" }}
            >
              <button data-testid="hero-book-btn" onClick={onBookAppointment}
                className="hover:-translate-y-0.5 transition-all"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14,
                  background: "linear-gradient(135deg, #2F6FE4 0%, #1E4DB7 100%)", color: "#fff",
                  padding: "14px 28px", borderRadius: 12, border: "none",
                  boxShadow: "0 10px 25px rgba(47,111,228,0.25)", cursor: "pointer" }}>
                <CalendarCheck style={{ width: 18, height: 18 }} strokeWidth={1.5} />Book Appointment
              </button>
              <a href="tel:+917075659696" data-testid="hero-call-btn"
                className="hover:-translate-y-0.5 transition-all"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 14,
                  background: "#fff", color: "#2F6FE4", padding: "14px 28px", borderRadius: 12,
                  border: "1px solid #D6E2F0", textDecoration: "none" }}>
                <Phone style={{ width: 18, height: 18 }} strokeWidth={1.5} />Call Now
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* ── Premium Floating Trust Metrics Bar ── */}
      <style>{`
        .tm-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .tm-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 56px rgba(31,78,121,0.18), 0 4px 16px rgba(31,78,121,0.10), inset 0 1px 0 rgba(255,255,255,1) !important;
        }
        .tm-card:hover .tm-icon-wrap {
          box-shadow: 0 0 0 3px rgba(46,111,184,0.12), 0 4px 20px rgba(46,111,184,0.20), inset 0 1px 0 rgba(255,255,255,1) !important;
        }
        @keyframes tm-fadein {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .tm-animate {
          animation: tm-fadein 0.7s cubic-bezier(0.22,1,0.36,1) both;
        }
        @media (max-width: 767px) {
          .tm-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 0 !important; }
          .tm-grid > div { border-right: none !important; border-radius: 0 !important; border-bottom: 1px solid rgba(46,111,184,0.08) !important; padding: 18px 10px !important; }
          .tm-grid > div:nth-child(odd) { border-right: 1px solid rgba(46,111,184,0.08) !important; }
          .tm-grid > div:nth-last-child(-n+2) { border-bottom: none !important; }
        }
        /* Hero margins handled inline per section */
      `}</style>

      <div ref={tmRef} style={{ position: "relative", zIndex: 30, marginTop: "-20px" }}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {/* Gradient border shell */}
          <div style={{
            borderRadius: 24,
            padding: "1.5px",
            background: "linear-gradient(135deg, rgba(46,111,184,0.28) 0%, rgba(43,187,173,0.15) 50%, rgba(255,255,255,0.7) 100%)",
          }}>
            <div style={{
              position: "relative",
              borderRadius: 23,
              background: "rgba(255,255,255,0.74)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
              boxShadow: "0 16px 56px rgba(31,78,121,0.14), 0 2px 8px rgba(31,78,121,0.06), inset 0 1px 0 rgba(255,255,255,0.95)",
              overflow: "hidden",
            }}>
              {/* Radial shimmer top */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 80, pointerEvents: "none",
                background: "radial-gradient(ellipse 70% 100% at 30% 0%, rgba(255,255,255,0.6) 0%, transparent 70%)",
              }} />

              <div className="tm-grid" style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                position: "relative",
              }}>
                {[
                  ...TM_METRICS,
                ].map((m, i) => (
                  <div
                    key={m.label}
                    className="tm-card tm-animate"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "30px 20px 26px",
                      gap: 14,
                      animationDelay: `${i * 100 + 500}ms`,
                      borderRight: i < 3 ? "1px solid rgba(46,111,184,0.08)" : "none",
                      cursor: "default",
                    }}
                  >
                    {/* Icon container — neumorphic glass */}
                    <div
                      className="tm-icon-wrap"
                      style={{
                        width: 62, height: 62,
                        borderRadius: 18,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.85) 100%)",
                        border: "1px solid rgba(46,111,184,0.12)",
                        boxShadow: "0 2px 14px rgba(31,78,121,0.10), inset 0 1px 0 rgba(255,255,255,1), inset 0 -1px 0 rgba(46,111,184,0.06)",
                        transition: "box-shadow 0.3s ease",
                        flexShrink: 0,
                      }}
                    >
                      {/* Drop your icon here — img or inline SVG */}
                      <img
                        src={m.iconSrc}
                        alt={m.iconAlt}
                        style={{ width: 32, height: 32, objectFit: "contain", display: "block" }}
                        onError={e => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextSibling.style.display = "block";
                        }}
                      />
                      {/* Fallback dot shown when icon file missing */}
                      <div style={{
                        display: "none", width: 28, height: 28, borderRadius: 8,
                        background: "linear-gradient(135deg, rgba(46,111,184,0.25), rgba(43,187,173,0.22))"
                      }} />
                    </div>

                    {/* Metric value */}
                    <div style={{ textAlign: "center" }}>
                      <AnimatedCounter target={m.target} suffix={m.suffix} decimal={m.decimal} inView={tmInView} />
                      <p style={{
                        marginTop: 7,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: "#6B8299",
                        textTransform: "uppercase",
                        letterSpacing: "0.09em",
                        margin: "7px 0 0 0",
                      }}>{m.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
